import { useState, useEffect, useCallback } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";
import { toast } from "@/hooks/use-toast";
import { GraftonChat } from "@/components/GraftonChat";
import {
  Phone, PhoneCall, PhoneOff, Plus, Upload, Search, User, Mail, Building2,
  MessageSquare, Clock, Send, Calendar, Star, Trash2, Edit, X, ChevronLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  company: string;
  notes: string;
  category: string;
  is_favorite: boolean;
}

interface ScheduledMessage {
  id: string;
  contact_id: string;
  message: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
}

interface CallLog {
  id: string;
  contact_id: string | null;
  contact_name: string;
  direction: string;
  duration: number;
  status: string;
  called_at: string;
}

// ── Animated Call Header ──
const CallAnimation = ({ isActive }: { isActive: boolean }) => (
  <div className="flex items-center justify-center gap-8 py-6">
    <div className={`relative flex items-center justify-center w-16 h-16 rounded-full transition-all duration-500 ${isActive ? "bg-green-500/20 shadow-[0_0_30px_hsl(142,70%,45%/0.3)]" : "bg-muted"}`}>
      <Phone className={`h-7 w-7 transition-colors ${isActive ? "text-green-500" : "text-muted-foreground"}`} />
      {isActive && <span className="absolute inset-0 rounded-full animate-ping bg-green-500/20" />}
    </div>
    <div className="flex items-center gap-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`block w-2.5 h-2.5 rounded-full transition-all ${isActive ? "bg-primary animate-bounce" : "bg-muted-foreground/30"}`}
          style={{ animationDelay: `${i * 150}ms`, animationDuration: "1s" }}
        />
      ))}
    </div>
    <div className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-500 ${isActive ? "bg-primary/20 shadow-[0_0_30px_hsl(var(--primary)/0.3)]" : "bg-muted"}`}>
      <User className={`h-7 w-7 transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`} />
    </div>
  </div>
);

export default function CallsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [search, setSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showContactDetail, setShowContactDetail] = useState(false);

  // New contact form
  const [newContact, setNewContact] = useState({ first_name: "", last_name: "", phone: "", email: "", company: "", notes: "", category: "client" });
  // Message form
  const [messageText, setMessageText] = useState("");
  const [messageContactId, setMessageContactId] = useState("");
  // Schedule form
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("09:00");
  const [schedContactId, setSchedContactId] = useState("");
  const [schedMessage, setSchedMessage] = useState("");

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [cRes, sRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/calls/contacts`, { credentials: "include" }),
        fetch(`${API_URL}/api/calls/scheduled`, { credentials: "include" }),
        fetch(`${API_URL}/api/calls/logs`, { credentials: "include" }),
      ]);
      if (cRes.ok) {
        const data = (await cRes.json()).contacts || [];
        setContacts(data as Contact[]);
        // Seed demo contacts if empty
        if (false as boolean) { // seed de démonstration désactivé : pas de faux contacts
          const demos = [
            { first_name: "Marie", last_name: "Dupont", phone: "+33 6 12 34 56 78", email: "marie@dupont.fr", company: "Dupont & Co", category: "client" },
            { first_name: "Jean", last_name: "Martin", phone: "+33 6 98 76 54 32", email: "jean@martin.fr", company: "Martin SARL", category: "fournisseur" },
            { first_name: "Sophie", last_name: "Bernard", phone: "+33 6 55 44 33 22", email: "sophie@bernard.fr", company: "Bernard Conseil", category: "partenaire" },
          ];
          for (const d of demos) {
            await fetch(`${API_URL}/api/calls/contacts`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(d) });
          }
          const r = await fetch(`${API_URL}/api/calls/contacts`, { credentials: "include" });
          if (r.ok) setContacts((await r.json()).contacts || []);
        }
      }
      if (sRes.ok) setScheduledMessages((await sRes.json()).messages || []);
      if (lRes.ok) setCallLogs((await lRes.json()).logs || []);
    } catch {}
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredContacts = contacts.filter((c) => {
    const q = search.toLowerCase();
    return `${c.first_name} ${c.last_name} ${c.company} ${c.phone}`.toLowerCase().includes(q);
  });

  const handleAddContact = async () => {
    if (!user || !newContact.first_name) return;
    await fetch(`${API_URL}/api/calls/contacts`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newContact) });
    setNewContact({ first_name: "", last_name: "", phone: "", email: "", company: "", notes: "", category: "client" });
    setShowAddContact(false);
    fetchData();
    toast({ title: "Contact ajouté" });
  };

  const handleDeleteContact = async (id: string) => {
    await fetch(`${API_URL}/api/calls/contacts/${id}`, { method: "DELETE", credentials: "include" });
    if (selectedContact?.id === id) { setSelectedContact(null); setShowContactDetail(false); }
    fetchData();
    toast({ title: "Contact supprimé" });
  };

  const handleCall = (contact: Contact) => {
    if (!contact.phone) {
      toast({ title: "Ce contact n'a pas de numéro de téléphone", variant: "destructive" });
      return;
    }
    setSelectedContact(contact);
    setIsCallActive(true);
    // Ouvrir le lien tel: natif — sur Mac avec Continuité Apple, l'appel passe via l'iPhone
    window.open(`tel:${contact.phone}`, "_self");
    // Log call
    if (user) {
      fetch(`${API_URL}/api/calls/logs`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ contact_id: contact.id, contact_name: `${contact.first_name} ${contact.last_name}`, direction: "outgoing", status: "completed" }) }).then(() => fetchData());
    }
    toast({ title: `📞 Appel lancé vers ${contact.first_name} ${contact.last_name}` });
    setTimeout(() => setIsCallActive(false), 5000);
  };

  const handleSendMessage = () => {
    if (!messageText || !messageContactId) { toast({ title: "Sélectionnez un contact et écrivez un message", variant: "destructive" }); return; }
    const contact = contacts.find((c) => c.id === messageContactId);
    toast({ title: `Message envoyé vocalement à ${contact?.first_name ?? ""}` });
    setMessageText("");
    setMessageContactId("");
  };

  const handleScheduleMessage = async () => {
    if (!user || !schedMessage || !schedContactId || !schedDate) { toast({ title: "Remplissez tous les champs", variant: "destructive" }); return; }
    await fetch(`${API_URL}/api/calls/scheduled`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ contact_id: schedContactId, message: schedMessage, scheduled_date: schedDate, scheduled_time: schedTime }) });
    setShowSchedule(false);
    setSchedMessage(""); setSchedContactId(""); setSchedDate(""); setSchedTime("09:00");
    fetchData();
    toast({ title: "Message programmé" });
  };

  const handleDeleteScheduled = async (id: string) => {
    await fetch(`${API_URL}/api/calls/scheduled/${id}`, { method: "DELETE", credentials: "include" });
    fetchData();
  };

  const handleFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.vcf,.txt";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;
      const text = await file.text();
      const lines = text.split("\n").filter(Boolean);
      let imported = 0;
      for (const line of lines.slice(1)) {
        const [first_name, last_name, phone, email, company] = line.split(",").map((s: string) => s.trim().replace(/"/g, ""));
        if (first_name) {
          await fetch(`${API_URL}/api/calls/contacts`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ first_name: first_name || "", last_name: last_name || "", phone: phone || "", email: email || "", company: company || "" }) });
          imported++;
        }
      }
      fetchData();
      toast({ title: `${imported} contacts importés ✓` });
    };
    input.click();
  };

  const categoryColors: Record<string, string> = {
    client: "bg-green-500/10 text-green-600 border-green-500/20",
    prospect: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    fournisseur: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    partenaire: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  };

  const getContactName = (id: string) => {
    const c = contacts.find((c) => c.id === id);
    return c ? `${c.first_name} ${c.last_name}` : "Inconnu";
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Appels & Messages</h1>
              <p className="text-sm text-muted-foreground">Gérez vos contacts, appels et messages vocaux</p>
            </div>
          </div>

          {/* Call Animation */}
          <Card className="mb-6 border-border/50 bg-card/50 backdrop-blur">
            <CardContent className="p-4">
              <CallAnimation isActive={isCallActive} />
              {isCallActive && selectedContact && (
                <div className="text-center">
                  <p className="text-sm font-medium text-green-500 animate-pulse">Appel en cours...</p>
                  <p className="text-lg font-bold">{selectedContact.first_name} {selectedContact.last_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedContact.phone}</p>
                  <Button variant="destructive" size="sm" className="mt-3" onClick={() => setIsCallActive(false)}>
                    <PhoneOff className="h-4 w-4 mr-1" /> Raccrocher
                  </Button>
                </div>
              )}
              {!isCallActive && (
                <p className="text-center text-sm text-muted-foreground">Cliquez sur un contact pour lancer un appel</p>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* ── Contacts ── */}
            <Card className="lg:col-span-2 border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Contacts</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleFileUpload}><Upload className="h-3.5 w-3.5 mr-1" /> Importer</Button>
                    <Button size="sm" onClick={() => setShowAddContact(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter</Button>
                  </div>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Rechercher un contact..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[320px]">
                  <div className="space-y-1">
                    {filteredContacts.map((c) => (
                      <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-accent/50 transition-colors group cursor-pointer"
                        onClick={() => { setSelectedContact(c); setShowContactDetail(true); }}>
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                          {c.first_name[0]}{c.last_name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.company || c.phone}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${categoryColors[c.category] || ""}`}>{c.category}</Badge>
                        <Button size="icon" variant="ghost" className="h-8 w-8 opacity-0 group-hover:opacity-100 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                          onClick={(e) => { e.stopPropagation(); handleCall(c); }}>
                          <PhoneCall className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDeleteContact(c.id); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {filteredContacts.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Aucun contact trouvé</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* ── Historique appels ── */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Derniers appels</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[320px]">
                  <div className="space-y-2">
                    {callLogs.map((log) => (
                      <div key={log.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${log.direction === "outgoing" ? "bg-blue-500/10 text-blue-500" : "bg-green-500/10 text-green-500"}`}>
                          <Phone className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{log.contact_name}</p>
                          <p className="text-[10px] text-muted-foreground">{new Date(log.called_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px]">{log.direction === "outgoing" ? "Sortant" : "Entrant"}</Badge>
                      </div>
                    ))}
                    {callLogs.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Aucun appel récent</p>}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* ── Messages Section ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Composer un message vocal */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Message vocal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select value={messageContactId} onValueChange={setMessageContactId}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Sélectionner un contact" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea placeholder="Écrivez votre message... Il sera lu vocalement au contact." value={messageText} onChange={(e) => setMessageText(e.target.value)} rows={3} />
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={handleSendMessage}><Send className="h-4 w-4 mr-1" /> Envoyer vocalement</Button>
                  <Button variant="outline" onClick={() => { setShowSchedule(true); setSchedMessage(messageText); setSchedContactId(messageContactId); }}>
                    <Calendar className="h-4 w-4 mr-1" /> Programmer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Messages programmés */}
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Messages programmés</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[180px]">
                  <div className="space-y-2">
                    {scheduledMessages.filter((m) => m.status === "scheduled").map((m) => (
                      <div key={m.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">{getContactName(m.contact_id)}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{m.message}</p>
                          <p className="text-[10px] text-primary">{m.scheduled_date} à {m.scheduled_time}</p>
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteScheduled(m.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {scheduledMessages.filter((m) => m.status === "scheduled").length === 0 && (
                      <p className="text-center text-sm text-muted-foreground py-6">Aucun message programmé</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* ── Grafton Chat ── */}
          <div className="mt-6">
            <GraftonChat
              context={{ contacts: contacts.length, callLogs: callLogs.length, scheduledMessages: scheduledMessages.length }}
              quickPrompts={["Propose une stratégie d'appels pour cette semaine", "Quels contacts devrais-je relancer ?", "Aide-moi à préparer un message pour un prospect"]}
            />
          </div>

          {/* ── Dialog: Add Contact ── */}
          <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Ajouter un contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Prénom" value={newContact.first_name} onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })} />
                  <Input placeholder="Nom" value={newContact.last_name} onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })} />
                </div>
                <Input placeholder="Téléphone" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} />
                <Input placeholder="Email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                <Input placeholder="Entreprise" value={newContact.company} onChange={(e) => setNewContact({ ...newContact, company: e.target.value })} />
                <Select value={newContact.category} onValueChange={(v) => setNewContact({ ...newContact, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="fournisseur">Fournisseur</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Notes" value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} rows={2} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddContact(false)}>Annuler</Button>
                <Button onClick={handleAddContact}>Ajouter</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Dialog: Contact Detail ── */}
          <Dialog open={showContactDetail} onOpenChange={setShowContactDetail}>
            <DialogContent className="sm:max-w-md">
              {selectedContact && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                        {selectedContact.first_name[0]}{selectedContact.last_name[0]}
                      </div>
                      <div>
                        <p>{selectedContact.first_name} {selectedContact.last_name}</p>
                        <Badge variant="outline" className={`text-[10px] ${categoryColors[selectedContact.category] || ""}`}>{selectedContact.category}</Badge>
                      </div>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {selectedContact.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.phone}</span>
                      </div>
                    )}
                    {selectedContact.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.email}</span>
                      </div>
                    )}
                    {selectedContact.company && (
                      <div className="flex items-center gap-3 text-sm">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedContact.company}</span>
                      </div>
                    )}
                    {selectedContact.notes && (
                      <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">{selectedContact.notes}</p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => handleDeleteContact(selectedContact.id)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setShowContactDetail(false); handleCall(selectedContact); }}>
                      <PhoneCall className="h-4 w-4 mr-1" /> Appeler
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* ── Dialog: Schedule Message ── */}
          <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Programmer un message</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Select value={schedContactId} onValueChange={setSchedContactId}>
                  <SelectTrigger><SelectValue placeholder="Contact" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea placeholder="Message..." value={schedMessage} onChange={(e) => setSchedMessage(e.target.value)} rows={3} />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} />
                  <Input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSchedule(false)}>Annuler</Button>
                <Button onClick={handleScheduleMessage}>Programmer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </SidebarProvider>
  );
}
